import React, { useEffect, useMemo, useState } from 'react';
import styled, { css, CSSProp } from 'styled-components';
import { colors } from '../../../config/styles';
import AutosizeInput from '../../AutosizeInput';
import {
  IStyleFunctionOrObject,
  ITextFieldStyleProps,
  ITextFieldStyles,
  mergeStyleSets,
  TextField as FluentUiTextField,
} from '@fluentui/react';
import { assertCondition } from '../../../utils/types';
import { ZodEffects, ZodObject, ZodString } from 'zod';
import { theme } from '../../../styles/theme';

type EditableTextInputSize = 'XS' | 'S' | 'M' | 'L';

interface IProps {
  inputSize: EditableTextInputSize;
  value: string;
  onSubmit: (newValue: string) => void;
  placeholder?: string;
  fontColor?: string;
  limit: number;
  disabled?: boolean;
  allowSameValue?: boolean;
  allowEmpty?: boolean;
  multiline?: boolean;
  autoFocus?: boolean;
  onCancel?: () => void;
  validation?: ZodObject<{ value: ZodEffects<ZodString, string, string> }>;
}

// the following styles should be kept in sync with multilineInputStyles below
const editableTextInputSizeConfig: Record<EditableTextInputSize, CSSProp> = {
  XS: {
    height: '20px',
    fontSize: '12px',
    fontWeight: 500,
  },
  S: {
    height: '24px',
    fontSize: '14px',
    fontWeight: 500,
  },
  M: {
    height: '100%',
    fontSize: '16px',
    fontWeight: 600,
  },
  L: {
    height: '28px',
    fontSize: '20px',
    fontWeight: 600,
  },
};

const multilineDefaultStyle: IStyleFunctionOrObject<
  ITextFieldStyleProps,
  ITextFieldStyles
> = {
  field: {
    padding: '0 2px',
    '&:disabled': {
      backgroundColor: 'inherit',
      color: 'inherit',
    },
  },
  fieldGroup: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    '.ms-TextField:not(.is-disabled) &:not(:focus-within):hover': {
      borderColor: 'transparent',
      backgroundColor: theme.colors.blue500,
    },
  },
  root: {
    width: '100%',
  },
};

// the following styles should be kept in sync with editableTextInputSizeConfig above
const multilineInputStyles: Record<
  EditableTextInputSize,
  IStyleFunctionOrObject<ITextFieldStyleProps, ITextFieldStyles>
> = {
  XS: mergeStyleSets(multilineDefaultStyle, {
    field: {
      height: 18,
      fontSize: 10,
      lineHeight: 18,
      fontWeight: '400',
      color: theme.colors.secondary600,
    },
    fieldGroup: { minHeight: 18 },
  }),
  S: mergeStyleSets(multilineDefaultStyle, {
    field: { height: 24, fontSize: 14, lineHeight: 20, fontWeight: 500 },
    fieldGroup: { minHeight: 24 },
  }),
  M: mergeStyleSets(multilineDefaultStyle, {
    field: { height: 24, fontSize: 16, lineHeight: 24, fontWeight: 600 },
    fieldGroup: { minHeight: 24 },
  }),
  L: mergeStyleSets(multilineDefaultStyle, {
    field: { height: 32, fontSize: 24, lineHeight: 32, fontWeight: 600 },
    fieldGroup: { minHeight: 32 },
  }),
};

const TextInput = styled(AutosizeInput)<{
  $size: EditableTextInputSize;
  $fontColor?: string;
  $isError?: boolean;
}>`
  position: relative;
  padding-right: 2px;
  padding-left: 2px;
  border: 1px solid transparent;
  border-radius: 2px;
  ${({ $size }) => $size && editableTextInputSizeConfig[$size]}
  color: ${({ $fontColor }) => $fontColor || theme.colors.secondary900};
  &:focus-within {
    border-color: ${({ $isError }) =>
      $isError ? colors.mainError : theme.colors.mainBlue};
  }
  ${({ disabled }) =>
    !disabled &&
    css`
      &:not(:focus-within):hover {
        background-color: ${theme.colors.blue500};
        cursor: text;
      }
    `}
  & {
    input,
    &::after {
      white-space: nowrap;
      overflow-x: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const EditableTextInput = ({
  onSubmit,
  value,
  placeholder,
  inputSize,
  fontColor,
  limit,
  disabled,
  allowSameValue,
  allowEmpty,
  multiline,
  autoFocus,
  onCancel,
  validation,
}: IProps) => {
  // We need draft value for Autosize input to work correctly
  const [draftValue, setDraftValue] = useState(value);

  const isValidationError: boolean = useMemo(() => {
    return !!validation && !validation.safeParse({ value: draftValue }).success;
  }, [draftValue, validation]);

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    assertCondition(
      event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement,
    );

    const target = event.target;

    if (event.key === 'Enter') {
      target.blur();
    }

    // Click on Escape will discard changes
    if (event.key === 'Escape') {
      if (onCancel) {
        onCancel();
      } else {
        target.value = value;
        setDraftValue(value);
        target.blur();
      }
    }
  };

  const handleInput = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    assertCondition(
      event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement,
    );

    const newValue = event.target.value;

    if (newValue.length <= limit) {
      setDraftValue(newValue);
    } else {
      event.preventDefault();
    }
  };

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    event.target.select();
  };

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    assertCondition(
      event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement,
    );

    const trimmedValue = event.target.value.trim();

    // If value hasn't changed and we're not allowing same value submissions
    if (!allowSameValue && trimmedValue === value) {
      if (onCancel) {
        onCancel();
      }
      return;
    }

    // Reset draft value if empty value is not allowed
    if (!trimmedValue.length && !allowEmpty) {
      setDraftValue(value);
      if (onCancel) {
        onCancel();
      }
      return;
    }

    if (
      !!validation &&
      !validation.safeParse({ value: trimmedValue }).success
    ) {
      setDraftValue(value);
      if (onCancel) {
        onCancel();
      }
      return;
    }

    onSubmit(trimmedValue);
  };

  // This is a workaround for EditableTextInput to update draftValue when value is changed
  // KB-7507 - when <Dashboard /> is initial launched, the value of description is empty string.
  // and then description is updated after the dashboard is loaded.
  // This could cause the description input to be empty even though the value is updated.
  useEffect(() => {
    setDraftValue((prevValue) => {
      return value !== prevValue ? value : prevValue;
    });
  }, [value]);

  if (multiline) {
    return (
      <FluentUiTextField
        value={draftValue}
        placeholder={placeholder}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onChange={handleInput}
        onBlur={handleBlur}
        disabled={disabled}
        multiline
        autoAdjustHeight
        resizable={false}
        styles={multilineInputStyles[inputSize]}
      />
    );
  } else {
    return (
      <TextInput
        value={draftValue}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onBlur={handleBlur}
        disabled={disabled}
        $size={inputSize}
        $fontColor={fontColor}
        $isError={isValidationError}
      />
    );
  }
};

export default EditableTextInput;
